// src/pages/inventory/InventoryManagement.jsx
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Table,
    Button,
    Form,
    Row,
    Col,
    InputGroup,
    Alert,
    Spinner,
    Modal,
    Badge,
    Tabs,
    Tab,
} from "react-bootstrap";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaFilter,
    FaTimes,
    FaBoxes,
    FaCheckCircle,
    FaExclamationTriangle,
    FaLock,
    FaInfoCircle,
    FaHistory,
    FaBell,
    FaEye,
    FaArrowLeft,
    FaGasPump,
    FaRupeeSign,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { stockApi } from "../../api/tenant/stock.api";
import {
    getProducts,
    deleteProduct,
} from "../../api/tenant/inventory.api";
import { cylinderTypeApi, brandApi } from "../../api/superadmin/masterData.api";
import "../../styles/styles.css";

const InventoryManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState("products");
    const [products, setProducts] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [summary, setSummary] = useState({
        total_products: 0,
        total_filled: 0,
        total_empty: 0,
        total_damaged: 0,
        total_reserved: 0,
        total_scrap: 0,
        total_stock: 0,
    });
    const [lowStockAlerts, setLowStockAlerts] = useState([]);
    const [expiryAlerts, setExpiryAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [stockStatusFilter, setStockStatusFilter] = useState("all");
    const [cylinderTypes, setCylinderTypes] = useState([]);
    const [brands, setBrands] = useState([]);
    const [filterType, setFilterType] = useState("all");
    const [filterBrand, setFilterBrand] = useState("all");
    const [filterStockType, setFilterStockType] = useState("all");
    const [showActionModal, setShowActionModal] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);
    const [actionData, setActionData] = useState({
        action: "purchase",
        quantity: 0,
        unitPrice: 0,
        remarks: "",
    });
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingStock, setViewingStock] = useState(null);
    const [showProductViewModal, setShowProductViewModal] = useState(false);
    const [viewingProduct, setViewingProduct] = useState(null);

    const STOCK_TYPES = {
        filled: { label: "Filled", color: "#027A48", bg: "#ECFDF3", icon: <FaCheckCircle /> },
        empty: { label: "Empty", color: "#6B7280", bg: "#F1F3F5", icon: <FaBoxes /> },
        damaged: { label: "Damaged", color: "#E11D48", bg: "#FFE4E6", icon: <FaExclamationTriangle /> },
        reserved: { label: "Reserved", color: "#437EF7", bg: "#D3EAFF", icon: <FaLock /> },
        scrap: { label: "Scrap", color: "#1A2A4A", bg: "#F1F3F5", icon: <FaTrash /> },
    };

    const stockStatusConfig = {
        inStock: { bg: "#ECFDF3", color: "#027A48", label: "In Stock" },
        lowStock: { bg: "#FEF6D7", color: "#FED229", label: "Low Stock" },
        outOfStock: { bg: "#FFDCE2", color: "#F94765", label: "Out of Stock" },
    };

    const categoryConfig = {
        "LPG Cylinder": { bg: "#D3EAFF", color: "#1e293b", label: "LPG Cylinder" },
        "Gas Stove": { bg: "#FFE0CB", color: "#FF8532", label: "Gas Stove" },
        Regulator: { bg: "#ECFDF3", color: "#027A48", label: "Regulator" },
        Pipe: { bg: "#FEF6D7", color: "#FED229", label: "Pipe" },
        Accessories: { bg: "#FFDCE2", color: "#F94765", label: "Accessories" },
        Other: { bg: "#F3F4F6", color: "#1e293b", label: "Other" },
        default: { bg: "#F3F4F6", color: "#1e293b", label: "Other" },
    };

    // Detect view type from URL
    const getViewType = () => {
        const path = location.pathname;
        if (path.includes('/filled')) return 'filled';
        if (path.includes('/empty')) return 'empty';
        if (path.includes('/damaged')) return 'damaged';
        if (path.includes('/reserved')) return 'reserved';
        if (path.includes('/management')) return 'management';
        if (path.includes('/transactions')) return 'transactions';
        if (path.includes('/alerts')) return 'alerts';
        if (path.includes('/report')) return 'report';
        return 'all';
    };

    const viewType = getViewType();

    useEffect(() => {
        // Set active tab based on view type
        if (viewType === 'alerts') {
            setActiveTab('alerts');
        } else if (['management', 'transactions', 'report'].includes(viewType)) {
            setActiveTab('stock');
        } else {
            setActiveTab('products');
        }
        
        // Apply stock type filter for specific views
        if (['filled', 'empty', 'damaged', 'reserved'].includes(viewType)) {
            setFilterStockType(viewType);
        } else {
            setFilterStockType('all');
        }
    }, [viewType]);

    useEffect(() => {
        loadAllData();
        loadDropdownData();
    }, []);

    // ✅ FIXED: Use getAll() instead of getBrands()
    const loadDropdownData = async () => {
        try {
            // Load cylinder types
            let typesData = [];
            try {
                const typesRes = await cylinderTypeApi.getAll();
                typesData = typesRes?.data?.data || typesRes?.data || [];
            } catch (error) {
                console.warn("Failed to load cylinder types:", error);
                typesData = [];
            }

            // ✅ FIXED: Use getAll() instead of getBrands()
            let brandsData = [];
            try {
                if (brandApi && typeof brandApi.getAll === 'function') {
                    const brandsRes = await brandApi.getAll();
                    brandsData = brandsRes?.data?.data || brandsRes?.data || [];
                } else {
                    console.warn("brandApi.getAll is not available");
                    brandsData = [];
                }
            } catch (error) {
                console.warn("Failed to load brands:", error);
                brandsData = [];
            }

            setCylinderTypes(Array.isArray(typesData) ? typesData : []);
            setBrands(Array.isArray(brandsData) ? brandsData : []);
        } catch (error) {
            console.error("Failed to load dropdown data:", error);
            setCylinderTypes([]);
            setBrands([]);
        }
    };

    // ✅ FIXED: Handle 404 errors gracefully
    const loadAllData = async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            // Load products
            let productsData = [];
            try {
                const productsRes = await getProducts({ limit: 200 });
                if (productsRes?.data?.data && Array.isArray(productsRes.data.data)) {
                    productsData = productsRes.data.data;
                } else if (productsRes?.data && Array.isArray(productsRes.data)) {
                    productsData = productsRes.data;
                } else if (Array.isArray(productsRes)) {
                    productsData = productsRes;
                }
            } catch (error) {
                console.warn("Failed to load products:", error);
                productsData = [];
            }
            setProducts(Array.isArray(productsData) ? productsData : []);

            // Load stock data
            let stockData = [];
            let summaryData = {
                total_products: 0,
                total_filled: 0,
                total_empty: 0,
                total_damaged: 0,
                total_reserved: 0,
                total_scrap: 0,
                total_stock: 0
            };
            
            try {
                const stockRes = await stockApi.getSummary({ limit: 200 });
                stockData = stockRes?.data?.data?.data || stockRes?.data?.data || stockRes?.data || [];
                summaryData = stockRes?.data?.summary || stockRes?.summary || summaryData;
            } catch (error) {
                console.warn("Failed to load stock data:", error);
                stockData = [];
            }
            setStocks(Array.isArray(stockData) ? stockData : []);
            setSummary(summaryData);

            // ✅ FIXED: Handle 404 for alerts with try-catch
            let lowAlerts = [];
            let expiryAlerts = [];
            
            try {
                const lowAlertsRes = await stockApi.getLowStockAlerts();
                lowAlerts = lowAlertsRes?.data?.data || lowAlertsRes?.data || [];
            } catch (error) {
                // Silently handle 404 - endpoint not implemented yet
                console.warn("Low stock alerts endpoint not available:", error.message);
                lowAlerts = [];
            }
            setLowStockAlerts(Array.isArray(lowAlerts) ? lowAlerts : []);

            try {
                const expiryAlertsRes = await stockApi.getExpiryAlerts();
                expiryAlerts = expiryAlertsRes?.data?.data || expiryAlertsRes?.data || [];
            } catch (error) {
                // Silently handle 404 - endpoint not implemented yet
                console.warn("Expiry alerts endpoint not available:", error.message);
                expiryAlerts = [];
            }
            setExpiryAlerts(Array.isArray(expiryAlerts) ? expiryAlerts : []);

        } catch (error) {
            console.error("Failed to load data:", error);
            setErrorMessage(error.response?.data?.message || "Failed to load data");
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const handleStockAction = async () => {
        if (!selectedStock) return;
        if (actionData.quantity <= 0) {
            toast.error("Please enter a valid quantity");
            return;
        }

        try {
            await stockApi.performAction({
                stockId: selectedStock.id,
                action: actionData.action,
                quantity: parseInt(actionData.quantity),
                unitPrice: parseFloat(actionData.unitPrice) || 0,
                remarks: actionData.remarks || null,
            });

            toast.success(`Stock ${actionData.action} completed successfully!`);
            setShowActionModal(false);
            loadAllData();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to perform stock action");
        }
    };

    const handleDeleteProduct = async (id, name) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Delete product "${name}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await deleteProduct(id);
                toast.success(`✅ Product "${name}" deleted successfully!`);
                loadAllData();
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to delete product");
            }
        }
    };

    const getStockStatus = (currentStock, minStockLevel) => {
        if (currentStock <= 0) return "outOfStock";
        if (currentStock <= minStockLevel) return "lowStock";
        return "inStock";
    };

    const getStockStatusBadge = (currentStock, minStockLevel) => {
        const status = getStockStatus(currentStock, minStockLevel);
        const config = stockStatusConfig[status];
        return (
            <span
                style={{
                    backgroundColor: config.bg,
                    color: config.color,
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontWeight: "600",
                    fontSize: "13px",
                    display: "inline-block",
                }}
            >
                {config.label}
            </span>
        );
    };

    const getCategoryBadge = (category) => {
        const config = categoryConfig[category] || categoryConfig.default;
        return (
            <span
                style={{
                    backgroundColor: config.bg,
                    color: config.color,
                    padding: "6px 14px",
                    borderRadius: "20px",
                    fontWeight: "600",
                    fontSize: "13px",
                    display: "inline-block",
                }}
            >
                {config.label}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const filteredProducts = products.filter((product) => {
        const matchesSearch =
            searchTerm === "" ||
            product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.category?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory =
            categoryFilter === "all" || product.category === categoryFilter;
        const stockStatus = getStockStatus(product.current_stock, product.min_stock_level);
        let matchesStockStatus = true;
        if (stockStatusFilter === "low") matchesStockStatus = stockStatus === "lowStock";
        else if (stockStatusFilter === "out") matchesStockStatus = stockStatus === "outOfStock";
        else if (stockStatusFilter === "in") matchesStockStatus = stockStatus === "inStock";
        return matchesSearch && matchesCategory && matchesStockStatus;
    });

    const filteredStocks = stocks.filter((stock) => {
        const matchesSearch =
            searchTerm === "" ||
            stock.cylinder_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            stock.cylinder_type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            stock.batch_number?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === "all" || stock.cylinder_type_id === parseInt(filterType);
        const matchesBrand = filterBrand === "all" || stock.brand_id === parseInt(filterBrand);

        let matchesStockType = true;
        if (filterStockType !== "all") {
            const stockTypeMap = {
                filled: "filled_stock",
                empty: "empty_stock",
                damaged: "damaged_stock",
                reserved: "reserved_stock",
                scrap: "scrap_stock",
            };
            const field = stockTypeMap[filterStockType];
            matchesStockType = (stock[field] || 0) > 0;
        }

        return matchesSearch && matchesType && matchesBrand && matchesStockType;
    });

    const categories = [...new Set(products.map((p) => p.category).filter(Boolean))];

    const stats = {
        total: products.length,
        totalValue: products.reduce((sum, p) => sum + (p.current_stock || 0) * (p.selling_price || 0), 0),
        lowStock: products.filter((p) => p.current_stock > 0 && p.current_stock <= p.min_stock_level).length,
        outOfStock: products.filter((p) => p.current_stock <= 0).length,
    };

    if (loading) {
        return (
            <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
                <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
                <div className="text-center py-5">
                    <Spinner animation="border" variant="secondary" size="lg" />
                    <h4 className="mt-3">Loading inventory data...</h4>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: "rgb(30, 58, 111)" }}>
                        <FaBoxes className="me-2" /> Inventory Management
                    </h2>
                    <p className="text-muted">Manage products, cylinder stock, and inventory alerts</p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={() => loadAllData()} style={{ borderRadius: "8px" }}>
                        <FaInfoCircle className="me-2" /> Refresh
                    </Button>
                    <Button
                        onClick={() => navigate("/inventory/add")}
                        style={{
                            backgroundColor: "rgb(30, 58, 111)",
                            border: "none",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            fontWeight: "600",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <FaPlus size={14} /> Add Product
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <Row className="g-3 mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Total Products</small>
                                    <h4 className="fw-bold mb-0">{stats.total}</h4>
                                </div>
                                <div style={{ backgroundColor: "#D3EAFF", padding: "8px", borderRadius: "10px" }}>
                                    <FaBoxes size={18} style={{ color: "#437EF7" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #027A48" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Stock Value</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#027A48" }}>₹{formatCurrency(stats.totalValue)}</h4>
                                </div>
                                <div style={{ backgroundColor: "#ECFDF3", padding: "8px", borderRadius: "10px" }}>
                                    <FaRupeeSign size={18} style={{ color: "#027A48" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #FED229" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Low Stock</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#FED229" }}>{stats.lowStock}</h4>
                                </div>
                                <div style={{ backgroundColor: "#FEF6D7", padding: "8px", borderRadius: "10px" }}>
                                    <FaExclamationTriangle size={18} style={{ color: "#FED229" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #E11D48" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Out of Stock</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#E11D48" }}>{stats.outOfStock}</h4>
                                </div>
                                <div style={{ backgroundColor: "#FFE4E6", padding: "8px", borderRadius: "10px" }}>
                                    <FaTimes size={18} style={{ color: "#E11D48" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Stock Type Summary Cards */}
            <Row className="g-3 mb-4">
                <Col md={2}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #027A48" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Filled</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#027A48" }}>{summary.total_filled || 0}</h4>
                                </div>
                                <div style={{ backgroundColor: "#ECFDF3", padding: "8px", borderRadius: "10px" }}>
                                    <FaCheckCircle size={18} style={{ color: "#027A48" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #6B7280" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Empty</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#6B7280" }}>{summary.total_empty || 0}</h4>
                                </div>
                                <div style={{ backgroundColor: "#F1F3F5", padding: "8px", borderRadius: "10px" }}>
                                    <FaBoxes size={18} style={{ color: "#6B7280" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #E11D48" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Damaged</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#E11D48" }}>{summary.total_damaged || 0}</h4>
                                </div>
                                <div style={{ backgroundColor: "#FFE4E6", padding: "8px", borderRadius: "10px" }}>
                                    <FaExclamationTriangle size={18} style={{ color: "#E11D48" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #437EF7" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Reserved</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#437EF7" }}>{summary.total_reserved || 0}</h4>
                                </div>
                                <div style={{ backgroundColor: "#D3EAFF", padding: "8px", borderRadius: "10px" }}>
                                    <FaLock size={18} style={{ color: "#437EF7" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid #1A2A4A" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Scrap</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "#1A2A4A" }}>{summary.total_scrap || 0}</h4>
                                </div>
                                <div style={{ backgroundColor: "#F1F3F5", padding: "8px", borderRadius: "10px" }}>
                                    <FaTrash size={18} style={{ color: "#1A2A4A" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm rounded-3" style={{ borderLeft: "4px solid rgb(30, 58, 111)" }}>
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>Total</small>
                                    <h4 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>{summary.total_stock || 0}</h4>
                                </div>
                                <div style={{ backgroundColor: "#D3EAFF", padding: "8px", borderRadius: "10px" }}>
                                    <FaBoxes size={18} style={{ color: "rgb(30, 58, 111)" }} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {errorMessage && (
                <Alert variant="danger" className="mb-4" onClose={() => setErrorMessage("")} dismissible>
                    {errorMessage}
                </Alert>
            )}

            {/* Main Tabs */}
            <Card className="border-0 shadow-sm rounded-3">
                <Card.Body className="p-0">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="border-bottom px-3 pt-2"
                        style={{ backgroundColor: "#f8f9fa", borderTopLeftRadius: "0.75rem", borderTopRightRadius: "0.75rem" }}
                    >
                        {/* Tab 1: Products */}
                        <Tab eventKey="products" title={<span><FaBoxes className="me-2" />Products</span>}>
                            <div className="p-3">
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <InputGroup size="sm">
                                            <InputGroup.Text style={{ backgroundColor: "#fff", borderRight: "none" }}>
                                                <FaSearch className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name or category..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{ borderLeft: "none" }}
                                            />
                                            {searchTerm && (
                                                <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                                                    <FaTimes />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select size="sm" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                            <option value="all">All Categories ({stats.total})</option>
                                            {categories.map((cat) => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select size="sm" value={stockStatusFilter} onChange={(e) => setStockStatusFilter(e.target.value)}>
                                            <option value="all">All Stock Status</option>
                                            <option value="in">In Stock</option>
                                            <option value="low">Low Stock ({stats.lowStock})</option>
                                            <option value="out">Out of Stock ({stats.outOfStock})</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <Button variant="outline-secondary" size="sm" onClick={() => { setSearchTerm(""); setCategoryFilter("all"); setStockStatusFilter("all"); }} className="w-100">
                                            <FaFilter /> Clear
                                        </Button>
                                    </Col>
                                </Row>

                                <div className="table-responsive">
                                    <Table hover size="sm" style={{ fontSize: "13px" }}>
                                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                                            <tr>
                                                <th>Product Name</th>
                                                <th>Category</th>
                                                <th>Price</th>
                                                <th>Stock</th>
                                                <th>Status</th>
                                                <th>GST</th>
                                                <th style={{ width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredProducts.length > 0 ? (
                                                filteredProducts.map((product) => (
                                                    <tr key={product.id}>
                                                        <td className="fw-semibold">{product.product_name}</td>
                                                        <td>{getCategoryBadge(product.category)}</td>
                                                        <td>₹{formatCurrency(product.selling_price)}</td>
                                                        <td>
                                                            <span className={product.current_stock <= product.min_stock_level ? "text-danger fw-bold" : ""}>
                                                                {product.current_stock || 0} {product.unit || "NOS"}
                                                            </span>
                                                        </td>
                                                        <td>{getStockStatusBadge(product.current_stock, product.min_stock_level)}</td>
                                                        <td><Badge bg="light" text="dark">{product.gst_rate || 18}%</Badge></td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    className="p-1 px-2"
                                                                    onClick={() => {
                                                                        setViewingProduct(product);
                                                                        setShowProductViewModal(true);
                                                                    }}
                                                                    title="View"
                                                                >
                                                                    <FaEye size={12} style={{ color: "#4361ee" }} />
                                                                </Button>
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    className="p-1 px-2"
                                                                    onClick={() => navigate(`/inventory/edit/${product.id}`)}
                                                                    title="Edit"
                                                                >
                                                                    <FaEdit size={12} style={{ color: "#ff9800" }} />
                                                                </Button>
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    className="p-1 px-2"
                                                                    onClick={() => handleDeleteProduct(product.id, product.product_name)}
                                                                    title="Delete"
                                                                >
                                                                    <FaTrash size={12} style={{ color: "#dc3545" }} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4">
                                                        <FaBoxes size={30} className="text-muted mb-2" />
                                                        <p className="text-muted mb-0">No products found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </Tab>

                        {/* Tab 2: Cylinder Stock */}
                        <Tab eventKey="stock" title={<span><FaBoxes className="me-2" />Cylinder Stock</span>}>
                            <div className="p-3">
                                <Row className="mb-3">
                                    <Col md={3}>
                                        <InputGroup size="sm">
                                            <InputGroup.Text style={{ backgroundColor: "#fff", borderRight: "none" }}>
                                                <FaSearch className="text-muted" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by type or batch..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{ borderLeft: "none" }}
                                            />
                                            {searchTerm && (
                                                <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                                                    <FaTimes />
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Select size="sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                            <option value="all">All Types</option>
                                            {cylinderTypes.map((type) => (
                                                <option key={type.id} value={type.id}>{type.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Select size="sm" value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}>
                                            <option value="all">All Brands</option>
                                            {brands.map((brand) => (
                                                <option key={brand.id} value={brand.id}>{brand.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Select size="sm" value={filterStockType} onChange={(e) => setFilterStockType(e.target.value)}>
                                            <option value="all">All Stock Types</option>
                                            <option value="filled">Filled</option>
                                            <option value="empty">Empty</option>
                                            <option value="damaged">Damaged</option>
                                            <option value="reserved">Reserved</option>
                                            <option value="scrap">Scrap</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Button variant="outline-secondary" size="sm" onClick={() => { setSearchTerm(""); setFilterType("all"); setFilterBrand("all"); setFilterStockType("all"); }} className="w-100">
                                            <FaFilter /> Clear
                                        </Button>
                                    </Col>
                                </Row>

                                <div className="table-responsive">
                                    <Table hover size="sm" style={{ fontSize: "13px" }}>
                                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                                            <tr>
                                                <th>Cylinder Type</th>
                                                <th>Brand</th>
                                                <th>Filled</th>
                                                <th>Empty</th>
                                                <th>Damaged</th>
                                                <th>Reserved</th>
                                                <th>Scrap</th>
                                                <th>Total</th>
                                                <th>Status</th>
                                                <th style={{ width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStocks.length > 0 ? (
                                                filteredStocks.map((stock) => {
                                                    const total = (stock.filled_stock || 0) + (stock.empty_stock || 0) +
                                                        (stock.damaged_stock || 0) + (stock.reserved_stock || 0) + (stock.scrap_stock || 0);
                                                    const available = (stock.filled_stock || 0) + (stock.empty_stock || 0);
                                                    const isCritical = available <= stock.min_stock_level;
                                                    const isLow = available <= stock.reorder_level;

                                                    return (
                                                        <tr key={stock.id}>
                                                            <td className="fw-semibold">{stock.cylinder_name || stock.cylinder_type_name || "N/A"}</td>
                                                            <td>{stock.brand_name || "N/A"}</td>
                                                            <td><Badge bg="success" pill>{stock.filled_stock || 0}</Badge></td>
                                                            <td><Badge bg="secondary" pill>{stock.empty_stock || 0}</Badge></td>
                                                            <td><Badge bg="danger" pill>{stock.damaged_stock || 0}</Badge></td>
                                                            <td><Badge bg="info" pill>{stock.reserved_stock || 0}</Badge></td>
                                                            <td><Badge bg="dark" pill>{stock.scrap_stock || 0}</Badge></td>
                                                            <td><strong>{total}</strong></td>
                                                            <td>
                                                                {isCritical ? <Badge bg="danger">Critical</Badge> :
                                                                    isLow ? <Badge bg="warning">Low</Badge> :
                                                                        <Badge bg="success">OK</Badge>}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-1">
                                                                    <Button
                                                                        variant="light"
                                                                        size="sm"
                                                                        className="p-1 px-2"
                                                                        onClick={() => {
                                                                            setSelectedStock(stock);
                                                                            setActionData({ action: "purchase", quantity: 0, unitPrice: 0, remarks: "" });
                                                                            setShowActionModal(true);
                                                                        }}
                                                                        title="Add Stock"
                                                                    >
                                                                        <FaPlus size={12} style={{ color: "#28a745" }} />
                                                                    </Button>
                                                                    <Button
                                                                        variant="light"
                                                                        size="sm"
                                                                        className="p-1 px-2"
                                                                        onClick={() => {
                                                                            setViewingStock(stock);
                                                                            setShowViewModal(true);
                                                                        }}
                                                                        title="View"
                                                                    >
                                                                        <FaEye size={12} style={{ color: "#4361ee" }} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <FaBoxes size={30} className="text-muted mb-2" />
                                                        <p className="text-muted mb-0">No stock records found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </Tab>

                        {/* Tab 3: Alerts */}
                        <Tab eventKey="alerts" title={<span><FaBell className="me-2" />Alerts</span>}>
                            <div className="p-3">
                                <Row>
                                    <Col md={6}>
                                        <h6 className="fw-bold mb-3">Low Stock Alerts</h6>
                                        <div className="table-responsive">
                                            <Table hover size="sm" style={{ fontSize: "13px" }}>
                                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                                    <tr>
                                                        <th>Cylinder Type</th>
                                                        <th>Available</th>
                                                        <th>Reorder Level</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lowStockAlerts.length > 0 ? (
                                                        lowStockAlerts.map((alert) => (
                                                            <tr key={alert.id}>
                                                                <td>{alert.cylinder_name || alert.cylinder_type_name || "N/A"}</td>
                                                                <td>{(alert.filled_stock || 0) + (alert.empty_stock || 0)}</td>
                                                                <td>{alert.reorder_level || 50}</td>
                                                                <td>
                                                                    <Badge bg={((alert.filled_stock || 0) + (alert.empty_stock || 0)) <= alert.min_stock_level ? 'danger' : 'warning'}>
                                                                        {((alert.filled_stock || 0) + (alert.empty_stock || 0)) <= alert.min_stock_level ? 'Critical' : 'Low'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" className="text-center py-3 text-muted">
                                                                No low stock alerts
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <h6 className="fw-bold mb-3">Expiry Alerts</h6>
                                        <div className="table-responsive">
                                            <Table hover size="sm" style={{ fontSize: "13px" }}>
                                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                                    <tr>
                                                        <th>Cylinder Type</th>
                                                        <th>Batch</th>
                                                        <th>Expiry Date</th>
                                                        <th>Days Left</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {expiryAlerts.length > 0 ? (
                                                        expiryAlerts.map((alert) => (
                                                            <tr key={alert.id}>
                                                                <td>{alert.cylinder_name || alert.cylinder_type_name || "N/A"}</td>
                                                                <td>{alert.batch_number || "N/A"}</td>
                                                                <td>{formatDate(alert.expiry_date)}</td>
                                                                <td>
                                                                    <span className={alert.days_until_expiry <= 30 ? 'text-danger fw-bold' : 'text-warning'}>
                                                                        {alert.days_until_expiry || "N/A"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <Badge bg={alert.days_until_expiry <= 30 ? 'danger' : 'warning'}>
                                                                        {alert.days_until_expiry <= 30 ? 'Critical' : 'Warning'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-3 text-muted">
                                                                No expiry alerts
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>

            {/* Stock Action Modal */}
            <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title><FaPlus className="me-2" /> Stock Action</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedStock && (
                        <Form>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Label className="text-muted" style={{ fontSize: "12px" }}>Cylinder Type</Form.Label>
                                    <div className="fw-semibold">{selectedStock.cylinder_name || selectedStock.cylinder_type_name || "N/A"}</div>
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="text-muted" style={{ fontSize: "12px" }}>Current Stock</Form.Label>
                                    <div className="fw-semibold">
                                        {(selectedStock.filled_stock || 0) + (selectedStock.empty_stock || 0)}
                                    </div>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Action Type</Form.Label>
                                <Form.Select
                                    value={actionData.action}
                                    onChange={(e) => setActionData({ ...actionData, action: e.target.value })}
                                >
                                    <option value="purchase">Purchase (Add Stock)</option>
                                    <option value="sale">Sale (Remove Stock)</option>
                                    <option value="return">Return</option>
                                    <option value="damage">Damage Write-off</option>
                                    <option value="scrap">Scrap</option>
                                    <option value="reserve">Reserve</option>
                                    <option value="unreserve">Unreserve</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Quantity</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={actionData.quantity}
                                    onChange={(e) => setActionData({ ...actionData, quantity: e.target.value })}
                                    min="1"
                                    placeholder="Enter quantity"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Unit Price (₹)</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={actionData.unitPrice}
                                    onChange={(e) => setActionData({ ...actionData, unitPrice: e.target.value })}
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Remarks</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={actionData.remarks}
                                    onChange={(e) => setActionData({ ...actionData, remarks: e.target.value })}
                                    placeholder="Optional remarks"
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowActionModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleStockAction} style={{ backgroundColor: "rgb(30, 58, 111)" }}>
                        <FaCheckCircle className="me-2" /> Execute
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Stock Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white rounded-top-3 border-0">
                    <Modal.Title><FaEye className="me-2" /> Stock Details</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {viewingStock && (
                        <Row>
                            <Col md={6}>
                                <InfoCard title="Product Information" icon={<FaGasPump />}>
                                    <InfoRow label="Cylinder Type" value={viewingStock.cylinder_name || viewingStock.cylinder_type_name || "N/A"} highlight />
                                    <InfoRow label="Brand" value={viewingStock.brand_name || "N/A"} />
                                    <InfoRow label="Batch Number" value={viewingStock.batch_number || "N/A"} />
                                    <InfoRow label="Location" value={viewingStock.location || "N/A"} />
                                    <InfoRow label="Rack Number" value={viewingStock.rack_number || "N/A"} />
                                    <InfoRow label="Manufacture Date" value={formatDate(viewingStock.manufacture_date)} />
                                    <InfoRow label="Expiry Date" value={formatDate(viewingStock.expiry_date)} />
                                </InfoCard>
                            </Col>
                            <Col md={6}>
                                <InfoCard title="Stock Details" icon={<FaBoxes />}>
                                    <InfoRow label="Filled Stock" value={viewingStock.filled_stock || 0} highlight />
                                    <InfoRow label="Empty Stock" value={viewingStock.empty_stock || 0} />
                                    <InfoRow label="Damaged Stock" value={viewingStock.damaged_stock || 0} />
                                    <InfoRow label="Reserved Stock" value={viewingStock.reserved_stock || 0} />
                                    <InfoRow label="Scrap Stock" value={viewingStock.scrap_stock || 0} />
                                    <InfoRow label="Total Stock" value={(viewingStock.filled_stock || 0) + (viewingStock.empty_stock || 0) + (viewingStock.damaged_stock || 0) + (viewingStock.reserved_stock || 0) + (viewingStock.scrap_stock || 0)} highlight />
                                    <InfoRow label="Stock Value" value={`₹${formatCurrency(viewingStock.stock_value || 0)}`} />
                                </InfoCard>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowViewModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* View Product Modal */}
            <Modal show={showProductViewModal} onHide={() => setShowProductViewModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white rounded-top-3 border-0">
                    <Modal.Title><FaEye className="me-2" /> Product Details</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {viewingProduct && (
                        <Row>
                            <Col md={6}>
                                <InfoCard title="Product Information" icon={<FaBoxes />}>
                                    <InfoRow label="Product Name" value={viewingProduct.product_name} highlight />
                                    <InfoRow label="Product Code" value={viewingProduct.product_code || "N/A"} />
                                    <InfoRow label="Category" value={getCategoryBadge(viewingProduct.category)} isHtml />
                                    <InfoRow label="HSN Code" value={viewingProduct.hsn_code || "N/A"} />
                                    <InfoRow label="Unit" value={viewingProduct.unit || "NOS"} />
                                    <InfoRow label="Brand" value={viewingProduct.brand_name || viewingProduct.brand?.name || "N/A"} />
                                </InfoCard>
                            </Col>
                            <Col md={6}>
                                <InfoCard title="Pricing & Stock" icon={<FaRupeeSign />}>
                                    <InfoRow label="Purchase Price" value={`₹${formatCurrency(viewingProduct.purchase_price)}`} />
                                    <InfoRow label="Selling Price" value={`₹${formatCurrency(viewingProduct.selling_price)}`} highlight />
                                    <InfoRow label="GST Rate" value={`${viewingProduct.gst_rate || 18}%`} />
                                    <InfoRow label="Current Stock" value={`${viewingProduct.current_stock || 0} ${viewingProduct.unit || "NOS"}`} />
                                    <InfoRow label="Min Stock Level" value={`${viewingProduct.min_stock_level || 0} ${viewingProduct.unit || "NOS"}`} />
                                    <InfoRow label="Status" value={getStockStatusBadge(viewingProduct.current_stock, viewingProduct.min_stock_level)} isHtml />
                                </InfoCard>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowProductViewModal(false)}>Close</Button>
                    <Button variant="primary" onClick={() => { setShowProductViewModal(false); navigate(`/inventory/edit/${viewingProduct?.id}`); }}>
                        <FaEdit className="me-2" /> Edit
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

// Helper Components
const InfoCard = ({ title, icon, children }) => (
    <Card className="border-0 shadow-sm rounded-3 h-100">
        <Card.Header className="bg-white border-bottom pt-3 pb-2">
            <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>{icon} {title}</h6>
        </Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const InfoRow = ({ label, value, isHtml, highlight }) => (
    <div className="mb-2 pb-2 border-bottom">
        <small className="text-muted">{label}</small>
        <div className={highlight ? "fw-bold" : ""} style={highlight ? { color: "rgb(30, 58, 111)" } : {}}>
            {isHtml ? value : <span>{value}</span>}
        </div>
    </div>
);

export default InventoryManagement;